//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { isEqual, memoize } from "lodash";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import Radium from "radium";
import { Helmet } from "react-helmet";
import { withRouter } from "found";
import MomentUtils from "@date-io/moment";
// modern-wrapper imports
import { IContainer, IProps, IState } from "./ModernWrapper.d";
import { modernWrapperFragments, modernWrapperQuery } from "./ModernWrapper.gql";
import * as s from "./ModernWrapper.styles";
// other components
// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
const Div100vh = require("react-div-100vh").default;
import { NetworkOffline } from "@clockwise/web-commons/src/components/network-offline";
import { ErrorPage } from "@components/error-page";
import { SnackBar } from "../snack-bar/";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setIsOnline } from "@actions/chrome-extension.actions";
import { setExperiments } from "@actions/auth.actions";
// util
import { getOnlineStatus } from "@util/health.util";
import { logger } from "@util/logger.util";
import { paths } from "@constants/site.constant";
import { getCurrentOrg } from "@util/org.util";
import { group, startFullStory } from "@util/analytics.util";
import { fromGlobalId } from "@util/graphql.util";
import { ZonedMoment } from "@util/time-zone.util";
import { recordDuration } from "@util/stats.util";
import { windowLocation } from "@util/location.util";
// import { PostMessageManager } from '@util/post-message.util';

import {
  initIdealDefrag,
  initDecorateEvents,
  initFilterRooms,
  initView,
  initExperiments,
} from "@util/clockwise-debugger.util";
// material-ui
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { withRouteQuery } from "@routing/routes.util";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { translate } from "@clockwise/client-commons/src/util/translations";
import { getBulkExperiments } from "@src/util/experiment.util";
import { cwMuiBase } from "@clockwise/design-system";
import { FlagsProvider } from "@clockwise/web-commons/src/util/FlagsContext";
import { FlagNamespaces } from "@clockwise/client-commons/src/util/flags";
import { ExperimentsProvider } from "@clockwise/web-commons/src/util/ExperimentsContext";

const muiTheme = createTheme(cwMuiBase);

//////////////////
// COMPONENT
//////////////////
class ModernWrapperCmpt extends React.Component<IProps, IState> {
  private isRetrying = false;
  private retryCounts = 0;
  private healthCheckTimer?: ReturnType<typeof setTimeout>;
  private hasStartedFS = false;

  constructor(props: IProps) {
    super(props);

    this.state = {
      boundaryError: false,
    };
  }

  private storageChangeHandler = (event: any) => {
    // listen for jwt changes
    if (event.key === "Jwt") {
      if (event.newValue && !event.oldValue) {
        // sign in
        if (this.props.isChromeExtension) {
          windowLocation.reload("ModernWrapperJwtChrome");
        } else if (this.props.location) {
          const isLogin =
            this.props.location.pathname.startsWith(paths.login) ||
            this.props.location.pathname.startsWith(paths.signup) ||
            this.props.location.pathname.startsWith(paths.welcome);
          if (isLogin) {
            windowLocation.assign("ModernWrapperLogin", "/app");
          }
        }
      } else if (event.oldValue && !event.newValue) {
        // sign out
        // PostMessageManager.openSidebar();
        windowLocation.reload("ModernWrapperLogout");
      }
    }
  };

  private checkHealth = () => {
    if (this.isRetrying) {
      return Promise.resolve();
    }

    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
      delete this.healthCheckTimer;
    }

    this.isRetrying = true;

    return getOnlineStatus().then((isOnline) => {
      if (isOnline) {
        if (!this.props.isOnline) {
          this.props.setIsOnline(true);
          recordDuration("client.reconnected", this.retryCounts, { project: "modern" });
        }
        this.healthCheckTimer = setTimeout(this.checkHealth, 10000);
      } else {
        if (this.props.isOnline) {
          this.props.setIsOnline(false);
          this.retryCounts = 0;
        }

        this.retryCounts++;
      }

      this.isRetrying = false;
    });
  };

  public componentDidCatch(error: Error) {
    logger.error("ModernWrapper componentDidCatch", error);

    this.setState({
      boundaryError: true,
    });
  }

  public componentDidMount() {
    // debugger interface
    initIdealDefrag(this);
    initDecorateEvents(this);
    initFilterRooms(this);
    const userStatus = this.props.viewer && this.props.viewer.user && this.props.viewer.user.status;
    initView(userStatus, this);
    initExperiments(this);

    this.checkHealth();

    // analytics
    const org = getCurrentOrg(this.props.viewer);
    if (org) {
      group(`Org::${fromGlobalId(org.id).id}`, {
        name: org.name,
        created: new ZonedMoment(org.createdTime),
      });

      if (org.experiments) {
        this.props.setExperiments(org.experiments);
      } else {
        logger.warn("User has org object but no experiments");
      }
    }

    // listen for storage changes to detect sign in
    window.addEventListener("storage", this.storageChangeHandler, false);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const org = getCurrentOrg(this.props.viewer);
    const nextOrg = getCurrentOrg(nextProps.viewer);

    if (org && nextOrg && !isEqual(org.experiments, nextOrg.experiments)) {
      this.props.setExperiments(nextOrg.experiments);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("storage", this.storageChangeHandler, false);
  }

  private onClick = () => {
    // fullstory will not start recording until a user actually clicks
    // afterwards, we let fullstory manage inactivity and sessions
    if (!this.hasStartedFS) {
      this.hasStartedFS = true;
      startFullStory(this.props.viewer?.user);
    }
  };

  public render(): JSX.Element {
    if (!this.props.isOnline) {
      return (
        <Div100vh
          style={{
            height: "100rvh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <MuiThemeProvider theme={muiTheme}>
            <NetworkOffline onRetry={this.checkHealth} />
          </MuiThemeProvider>
        </Div100vh>
      );
    }

    if (this.state.boundaryError) {
      return <ErrorPage errors={[]} />;
    }

    const description =
      "Clockwise optimizes your team's calendars to create more time in everyone's day. Get started for free.";

    const org = getCurrentOrg(this.props.viewer);
    const webAppFlagsErrorable = this.props.viewer.user?.webAppFlagsErrorable ?? null;
    const featureFlags = getBulkExperiments(org);
    const memoizedTranslate = memoize(translate);

    return (
      <Radium.StyleRoot>
        <MuiThemeProvider theme={muiTheme}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <ExperimentsProvider experiments={featureFlags}>
              <FlagsProvider
                namespace={FlagNamespaces.Webapp}
                flagsErrorable={webAppFlagsErrorable}
              >
                <TranslationContext.Provider value={memoizedTranslate()}>
                  <div style={s.modernStyles} onClick={() => this.onClick()}>
                    <Helmet>
                      <title>Clockwise</title>
                      <meta name="description" content={description} />
                      <meta property="og:description" content={description} />
                      <meta name="twitter:description" content={description} />
                      <meta itemProp="description" content={description} />
                    </Helmet>
                    {this.props.children}
                    {/* {this.props.viewer.user ? <TimeZoneDialog /> : ''} */}
                    <SnackBar />
                  </div>
                </TranslationContext.Provider>
              </FlagsProvider>
            </ExperimentsProvider>
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      </Radium.StyleRoot>
    );
  }
}

//////////////////
// REDUX
//////////////////
const ModernWrapperWithRouter = withRouter(ModernWrapperCmpt);

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    isChromeExtension: state.webExtension.isWebExtension,
    isOnline: state.webExtension.isOnline,
  };
}

const ModernWrapperRedux = connect(mapStateToProps, {
  setIsOnline,
  setExperiments,
})(ModernWrapperWithRouter);

//////////////////
// RELAY
//////////////////
export const ModernWrapper = createFragmentContainer<IContainer>(
  ModernWrapperRedux,
  modernWrapperFragments,
);

export const ModernWrapperPage = withRouteQuery(ModernWrapper, modernWrapperQuery);
